import React, { useState, useEffect, useRef } from 'react';
import { useCMS } from '../context/CMSContext';
import Meta from '../components/Meta';

function TimelineItem({ event, index }) {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={elementRef}
      className={`relative pl-8 sm:pl-12 transition-all duration-1000 ease-out transform ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Year Badge (Desktop left-aligned) */}
      <div className="hidden sm:flex absolute right-full mr-12 top-0 items-center justify-end h-12 w-24 text-right">
        <span className="text-2xl font-extrabold text-white font-mono tracking-wider">
          {event.year}
        </span>
      </div>

      {/* Timeline Dot/Icon */}
      <span className={`absolute -left-[21px] top-1 flex h-10 w-10 items-center justify-center rounded-full border-2 border-brand-dark-border ${event.iconColor} text-sm shadow-lg transition-transform duration-500 ${isVisible ? 'scale-100' : 'scale-0'}`}>
        <i className={event.icon} />
      </span>

      {/* Timeline Card */}
      <div className="bg-brand-dark-card border border-brand-dark-border rounded-lg p-6 sm:p-8 text-left transition-all duration-300 hover:border-white/10 hover:shadow-2xl hover:shadow-brand-red/5">
        {/* Mobile Year Badge */}
        <span className="inline-block sm:hidden text-brand-red font-bold text-lg font-mono tracking-wider mb-2">
          {event.year}
        </span>
        <h3 className="text-lg sm:text-xl font-bold text-white uppercase tracking-wide mb-3">
          {event.title}
        </h3>
        <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
          {event.desc}
        </p>
      </div>
    </div>
  );
}

export default function OmOs() {
  const { getPage, loading } = useCMS();
  const page = getPage('om-os');

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  const title = page?.title || "Om os | Chr. Nielsen & Søn V/Leif Nielsen";
  const metaDescription = page?.meta_description || "Virksomheden blev etableret i 1948 af Christian Nielsen...";
  const headline = page?.headline || "Om os";
  const contentHtml = page?.content || "";

  // Timeline events extracted verbatim from the scrape
  const timelineEvents = [
    {
      year: '1948',
      title: 'Virksomheden blev etableret af Christian Nielsen',
      desc: 'Christian Nielsen blev vognmand på en lidt tilfældig måde, da der var nogle som skyldte ham penge, men de havde ikke nogen, så de gav ham en lastbil i stedet for.',
      icon: 'fa-solid fa-truck-pickup',
      color: 'border-brand-red',
      iconColor: 'bg-brand-red text-white'
    },
    {
      year: '1948+',
      title: 'De første ruter',
      desc: 'Han startede med at køre varer for den lokale brugs i Veerst. Senere for Kronborg Mejeriet, hvor han hentede mælk til mejeriet fra de lokale gårde. Senere kørte han også ost for mejeriet i en pressenningsbil. Det har så bredt sig til flere mejerier senere hen.',
      icon: 'fa-solid fa-route',
      color: 'border-brand-green',
      iconColor: 'bg-brand-green text-white'
    },
    {
      year: '1990',
      title: 'Leif Nielsen bliver medejer',
      desc: 'I 1990 blev Leif Nielsen medejer af virksomheden.',
      icon: 'fa-solid fa-handshake',
      color: 'border-brand-red',
      iconColor: 'bg-brand-red text-white'
    },
    {
      year: '1993',
      title: 'Leif Nielsen overtager virksomheden',
      desc: 'I 1993 overtog han virksomheden.',
      icon: 'fa-solid fa-key',
      color: 'border-brand-green',
      iconColor: 'bg-brand-green text-white'
    },
    {
      year: '1997',
      title: 'Flytning og lagerbyggeri',
      desc: 'I 1997 flyttede virksomheden til der hvor den ligger i dag og byggede et lager.',
      icon: 'fa-solid fa-warehouse',
      color: 'border-brand-red',
      iconColor: 'bg-brand-red text-white'
    },
    {
      year: '2003',
      title: 'Tilbygning af kølerum',
      desc: 'I 2003 blev der tilbygget et kølerum til lageret.',
      icon: 'fa-solid fa-snowflake',
      color: 'border-brand-green',
      iconColor: 'bg-brand-green text-white'
    }
  ];

  return (
    <>
      <Meta title={title} description={metaDescription} pageTitle="Om os" />

      {/* Page Header */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/c3c69980-e6b4-4d46-bdae-26d6f6795887/lager-logistik.jpg" 
            alt="Chr. Nielsen & Søn Lager" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white uppercase tracking-tight">
            {headline}
          </h1>
          <div className="h-1.5 w-24 bg-brand-red mx-auto mt-6" />
          <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Læs historien om, hvordan en tilfældig gæld i 1948 lagde grundstenen til en moderne transportvirksomhed med speciale i køletransport.
          </p>
        </div>
      </section>

      {/* Main Content & Timeline */}
      <section className="pt-10 lg:pt-24 pb-24 bg-brand-dark">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          
          {/* Intro Text from CMS */}
          <div className="prose prose-invert max-w-none mb-20 text-left">
            <div 
              className="text-lg text-gray-300 leading-relaxed w-full space-y-6"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          </div>

          {/* Timeline Header */}
          <div className="text-center mb-16">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-brand-green mb-3">
              Vores Rejse Gennem Tiden
            </h2>
            <p className="text-xl sm:text-3xl font-extrabold text-white uppercase tracking-tight">
              Tidslinje over virksomhedens udvikling
            </p>
            <div className="h-1 w-16 bg-brand-green mx-auto mt-4" />
          </div>

          {/* Vertical Timeline */}
          <div className="relative border-l-2 border-brand-dark-border ml-4 sm:ml-32 space-y-12">
            {timelineEvents.map((event, idx) => (
              <TimelineItem key={idx} event={event} index={idx} />
            ))}
          </div>

        </div>
      </section>
    </>
  );
}
